import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AUTHORIZATION_CONTEXT_CHANGED_EVENT } from '../../features/auth/session-provider';
import {
  searchOrganizationExperience,
  type SmartSearchResult,
} from './smart-search.api';

export interface SmartSearchState {
  results: SmartSearchResult[];
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  partial: boolean;
  stale: boolean;
  retry: () => void;
}

function normalize(value: string): string {
  return value.normalize('NFKC').replace(/\s+/g, ' ').trim().toLocaleLowerCase();
}

function destinationKey(result: SmartSearchResult): string {
  const params = result.queryParams
    ? new URLSearchParams(Object.entries(result.queryParams).sort(([a], [b]) => a.localeCompare(b))).toString()
    : '';
  return `${result.routeId ?? ''}:${result.recordId ?? ''}:${params}`;
}

function localBrowseScore(result: SmartSearchResult, query: string): number {
  if (!query) return 1_000 + (result.browsePriority ?? 0);
  const values = [
    result.title,
    result.subtitle,
    result.labelKey,
    result.descriptionKey,
    result.entityLabelKey,
    result.routeId,
    ...(result.searchTerms ?? []),
  ].map((value) => normalize(value ?? '')).filter(Boolean);
  let best = 0;
  for (const value of values) {
    if (value === query) best = Math.max(best, 400);
    else if (value.split(/[^\p{L}\p{N}]+/u).some((token) => token === query)) best = Math.max(best, 360);
    else if (value.startsWith(query)) best = Math.max(best, 320);
    else if (value.split(/[^\p{L}\p{N}]+/u).some((token) => token.startsWith(query))) best = Math.max(best, 290);
    else if (query.length >= 2 && value.includes(query)) best = Math.max(best, 220);
  }
  return best ? best + Math.min(result.browsePriority ?? 0, 100) : 0;
}

function localBrowseResults(
  browse: readonly SmartSearchResult[],
  query: string,
  limit: number,
): SmartSearchResult[] {
  return browse
    .map((result) => ({ result, score: localBrowseScore(result, query) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.result.id.localeCompare(b.result.id))
    .slice(0, limit)
    .map((entry) => entry.result);
}

function mergeResults(
  remote: readonly SmartSearchResult[],
  local: readonly SmartSearchResult[],
  limit: number,
): SmartSearchResult[] {
  const merged: SmartSearchResult[] = [];
  const seen = new Set<string>();
  for (const result of [...remote, ...local]) {
    const key = destinationKey(result);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    merged.push(result);
    if (merged.length >= limit) break;
  }
  return merged;
}

/**
 * Smart Search keeps a small in-memory Browse cache scoped by the authenticated
 * authorization context. Reopening the palette in the same scope is instant,
 * while Branch/Client/portfolio/delegation/AAL changes use a different cache key.
 */
export function useSmartSearch(
  query: string,
  limit = 24,
  enabled = true,
  authorizationCacheKey = 'anonymous',
): SmartSearchState {
  const normalized = useMemo(() => normalize(query), [query]);
  const browseCacheRef = useRef<Map<string, SmartSearchResult[]>>(new Map());
  const generationRef = useRef(0);
  const [retryRevision, setRetryRevision] = useState(0);
  const [authorizationRevision, setAuthorizationRevision] = useState(0);
  const [state, setState] = useState<Omit<SmartSearchState, 'retry'>>({
    results: [],
    loading: false,
    refreshing: false,
    error: null,
    partial: false,
    stale: false,
  });

  const retry = useCallback(() => setRetryRevision((value) => value + 1), []);

  useEffect(() => {
    const invalidate = () => {
      browseCacheRef.current.clear();
      generationRef.current += 1;
      setAuthorizationRevision((value) => value + 1);
    };
    window.addEventListener(AUTHORIZATION_CONTEXT_CHANGED_EVENT, invalidate);
    return () => window.removeEventListener(AUTHORIZATION_CONTEXT_CHANGED_EVENT, invalidate);
  }, []);

  useEffect(() => {
    if (!enabled) {
      generationRef.current += 1;
      setState({
        results: [],
        loading: false,
        refreshing: false,
        error: null,
        partial: false,
        stale: false,
      });
      return;
    }

    const generation = ++generationRef.current;
    const controller = new AbortController();
    const browse = browseCacheRef.current.get(authorizationCacheKey) ?? [];
    const local = localBrowseResults(browse, normalized, limit);

    setState((previous) => ({
      results: local,
      loading: local.length === 0 && browse.length === 0,
      refreshing: local.length > 0 || previous.results.length > 0,
      error: null,
      partial: previous.partial && normalized.length > 0,
      stale: false,
    }));

    const timer = window.setTimeout(() => {
      void searchOrganizationExperience(normalized, { limit, signal: controller.signal })
        .then((response) => {
          if (controller.signal.aborted || generation !== generationRef.current) return;
          if (!normalized) {
            if (!browseCacheRef.current.has(authorizationCacheKey) && browseCacheRef.current.size >= 4) {
              const oldest = browseCacheRef.current.keys().next().value as string | undefined;
              if (oldest) browseCacheRef.current.delete(oldest);
            }
            browseCacheRef.current.set(authorizationCacheKey, response.results);
          }
          const currentBrowse = browseCacheRef.current.get(authorizationCacheKey) ?? [];
          const currentLocal = localBrowseResults(currentBrowse, normalized, limit);
          setState({
            results: normalized
              ? mergeResults(response.results, currentLocal, limit)
              : response.results,
            loading: false,
            refreshing: false,
            error: null,
            partial: Boolean(response.partial),
            stale: false,
          });
        })
        .catch((error) => {
          if (controller.signal.aborted || generation !== generationRef.current) return;
          const currentBrowse = browseCacheRef.current.get(authorizationCacheKey) ?? [];
          const currentLocal = localBrowseResults(currentBrowse, normalized, limit);
          setState({
            results: currentLocal,
            loading: false,
            refreshing: false,
            error: error instanceof Error ? error.message : 'SEARCH_FAILED',
            partial: false,
            stale: currentLocal.length > 0,
          });
        });
    }, normalized ? 100 : 0);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [authorizationCacheKey, authorizationRevision, enabled, limit, normalized, retryRevision]);

  return { ...state, retry };
}
